import React, {
  useState,
  useMemo,
  useCallback,
  ReactNode,
  useEffect,
} from 'react'
import { useDebouncedValue } from '@mantine/hooks'
import { SelectWithTable } from 'app/views/components/inputs/selects/CustomSelectWithTable'

interface GenericSearchProps<T> {
  data: T[]
  placeholder?: string
  searchableFields: (keyof T)[]
  onItemSelect: (item: T) => void
  renderTable: (
    data: T[],
    searchValue: string,
    onItemSelect: (item: T) => void,
    onClose: () => void,
  ) => ReactNode
  width?: number
  dropdownWidth?: number
  minSearchLength?: number
  className?: string
  transformX?: number
  storageKey?: string
}

export function GenericSearch<T extends Record<string, any>>({
  data,
  placeholder = 'Search',
  searchableFields,
  onItemSelect,
  renderTable,
  width = 300,
  dropdownWidth,
  minSearchLength = 2,
  className,
  transformX,
  storageKey,
}: GenericSearchProps<T>) {
  const loadSearchValue = () => {
    if (storageKey) {
      try {
        return localStorage.getItem(storageKey) || ''
      } catch (error) {
        console.error('Error loading search value from localStorage:', error)
      }
    }
    return ''
  }

  const [searchValue, setSearchValue] = useState(loadSearchValue)
  const [dropdownOpen, setDropdownOpen] = useState(false)

  useEffect(() => {
    if (storageKey) {
      try {
        if (searchValue) {
          localStorage.setItem(storageKey, searchValue)
        } else {
          localStorage.removeItem(storageKey)
        }
      } catch (error) {
        console.error('Error saving search value to localStorage:', error)
      }
    }
  }, [searchValue, storageKey])

  const [debouncedSearchValue] = useDebouncedValue(searchValue, 300)

  const searchableData = useMemo(
    () =>
      data.map(item => ({
        ...item,
        searchableText: searchableFields
          .map(field => String(item[field] || ''))
          .filter(Boolean)
          .join(' ')
          .toLowerCase(),
      })),
    [data, searchableFields],
  )

  const filteredData = useMemo(() => {
    const trimmedSearch = debouncedSearchValue.trim()

    if (trimmedSearch.length < minSearchLength) {
      return []
    }

    const searchTerm = trimmedSearch.toLowerCase()

    return searchableData.filter(item =>
      item.searchableText.includes(searchTerm),
    )
  }, [searchableData, debouncedSearchValue, minSearchLength])

  const handleItemSelect = useCallback(
    (item: T) => {
      setDropdownOpen(false)
      onItemSelect(item)
    },
    [onItemSelect],
  )

  const handleClose = useCallback(() => {
    setDropdownOpen(false)
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur()
    }
  }, [])

  return (
    <SelectWithTable
      searchValue={searchValue}
      open={dropdownOpen}
      setOpen={setDropdownOpen}
      setSearchValue={setSearchValue}
      position="bottom-end"
      width={width}
      dropdownWidth={dropdownWidth}
      placeholder={placeholder}
      withArrow={false}
      className={className || 'generic-search-container'}
      transformX={transformX}
    >
      {renderTable(filteredData, searchValue, handleItemSelect, handleClose)}
    </SelectWithTable>
  )
}
